"use client";

import React from "react";
import { useOutletDelivery } from "@/src/hooks/delivery/useOutletDelivery";

interface DeliveryEstimateProps {
  /** Pages that receive an outletId as a prop should pass it — the tenant
   *  context is only populated after the landing page has rendered. */
  outletId?: string;
  /** Append "· 2.4 km" — worth the room on the detail page, not on a card. */
  showDistance?: boolean;
  /** Light-on-dark palette, for the deal hero and other dark surfaces. */
  onDark?: boolean;
  className?: string;
}

const TONE_COLORS: Record<string, { fg: string; bg: string }> = {
  fast: { fg: "var(--coral)", bg: "rgba(242, 98, 80, 0.10)" },
  standard: { fg: "var(--techno-black-70, #4A4A45)", bg: "rgba(40, 30, 15, 0.06)" },
  blocked: { fg: "#9A3B2C", bg: "rgba(154, 59, 44, 0.09)" },
};

const TONE_COLORS_DARK: Record<string, { fg: string; bg: string }> = {
  fast: { fg: "var(--turmeric, #E8B33A)", bg: "rgba(255,255,255,0.12)" },
  standard: { fg: "rgba(255,255,255,0.82)", bg: "rgba(255,255,255,0.10)" },
  blocked: { fg: "#FFC9BE", bg: "rgba(154, 59, 44, 0.35)" },
};

/**
 * Per-outlet delivery line for a product card.
 *
 * The label is portal-api's, measured from the buyer's SELECTED address to the
 * outlet, so the same product reads "Instant" for a neighbour and "4-7 days"
 * for someone a state away.
 *
 * Renders NOTHING when the estimate is unknown — a guest with no saved address,
 * an address stored without coordinates, or a lookup that failed. A card that
 * silently drops the line is honest; one that falls back to a generic
 * "Same-day delivery" is not, and that generic claim on every product of every
 * store is exactly what this replaces.
 */
export const DeliveryEstimate: React.FC<DeliveryEstimateProps> = ({
  outletId,
  showDistance = false,
  onDark = false,
  className = "",
}) => {
  const { label, tone, distanceText } = useOutletDelivery(outletId);
  if (!label) return null;

  const palette = onDark ? TONE_COLORS_DARK : TONE_COLORS;
  const { fg, bg } = palette[tone] ?? palette.standard;

  return (
    <span
      className={`inline-flex max-w-full items-center gap-1 self-start rounded-full px-2 py-0.5 text-[10px] font-semibold sm:text-[10.5px] ${className}`}
      style={{ color: fg, background: bg }}
    >
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
        style={{ flexShrink: 0 }}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
      <span className="truncate">
        {label}
        {showDistance && distanceText ? ` · ${distanceText} away` : ""}
      </span>
    </span>
  );
};
